import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api/client'
import type { PageResponse } from '@/lib/api/types'
import type {
  BalanceView, LeaveTypeDetailView, LeaveTypeView, RejoinView, RequestView, SeedPackResult,
} from '../types'

const BASE = '/v1/leave'

export function useLeaveTypes() {
  return useQuery({
    queryKey: ['leave', 'types'],
    queryFn: async () => (await apiClient.get<LeaveTypeView[]>(`${BASE}/types`)).data,
    // Leave types change a few times a year at most.
    staleTime: 60 * 60 * 1000,
  })
}

export function useMyBalances(year?: number) {
  return useQuery({
    queryKey: ['leave', 'balances', year],
    queryFn: async () => (await apiClient.get<BalanceView[]>(`${BASE}/my/balances`, { params: { year } })).data,
  })
}

/**
 * Somebody else's balances, for their profile.
 *
 * <p>The server allows this to leave approvers only, so the caller gates on `leave:approve`
 * rather than letting the request fail into an error card.
 */
export function useEmployeeLeaveBalances(employeeId: string | undefined, year?: number, enabled = true) {
  return useQuery({
    queryKey: ['leave', 'balances', 'employee', employeeId, year],
    enabled: Boolean(employeeId) && enabled,
    queryFn: async () =>
      (await apiClient.get<BalanceView[]>(`${BASE}/balances`, { params: { employeeId, year } })).data,
  })
}

export function useMyRequests() {
  return useQuery({
    queryKey: ['leave', 'requests'],
    queryFn: async () => (await apiClient.get<RequestView[]>(`${BASE}/my/requests`)).data,
  })
}

export interface ApplyLeaveInput {
  leaveTypeId: string
  fromDate: string
  toDate: string
  dayPart?: string
  reason?: string
}

export function useLeaveMutations() {
  const queryClient = useQueryClient()
  // Applying moves a balance, so both the history and the balances must refresh.
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['leave'] })

  return {
    apply: useMutation({
      mutationFn: async (input: ApplyLeaveInput) =>
        (await apiClient.post<RequestView>(`${BASE}/requests`, input)).data,
      onSuccess: invalidate,
    }),
    cancel: useMutation({
      mutationFn: async ({ id, reason }: { id: string; reason?: string }) =>
        (await apiClient.post<RequestView>(`${BASE}/requests/${id}/cancel`, { reason })).data,
      onSuccess: invalidate,
    }),
  }
}

// --- return from leave ----------------------------------------------------

export interface ConfirmRejoinInput {
  id: string
  actualReturnDate: string
  lateTreatment?: string
  extensionRequestId?: string
  airTicketClaimId?: string
  remarks?: string
}

export function useRejoinMutations() {
  const queryClient = useQueryClient()
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['leave'] })

  return {
    confirm: useMutation({
      mutationFn: async ({ id, ...body }: ConfirmRejoinInput) =>
        (await apiClient.post<RejoinView>(`${BASE}/requests/${id}/rejoin`, body)).data,
      onSuccess: invalidate,
    }),
  }
}

// --- leave type administration -------------------------------------------
// Separate from useLeaveTypes, which feeds the apply-for-leave dropdown and stays a six-field
// summary. Only this screen needs accrual thresholds and pay bands.

export function useLeaveTypeAdmin() {
  return useQuery({
    queryKey: ['leave', 'admin', 'types'],
    queryFn: async () =>
      (await apiClient.get<LeaveTypeDetailView[]>(`${BASE}/admin/types`)).data,
  })
}

export function useLeaveTypeMutations() {
  const queryClient = useQueryClient()
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['leave'] })

  return {
    create: useMutation({
      mutationFn: async (body: Record<string, unknown>) =>
        (await apiClient.post<LeaveTypeDetailView>(`${BASE}/admin/types`, body)).data,
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: async ({ id, ...body }: { id: string } & Record<string, unknown>) =>
        (await apiClient.put<LeaveTypeDetailView>(`${BASE}/admin/types/${id}`, body)).data,
      onSuccess: invalidate,
    }),
    replaceTiers: useMutation({
      mutationFn: async ({ id, tiers }: { id: string; tiers: unknown[] }) =>
        (await apiClient.put<LeaveTypeDetailView>(`${BASE}/admin/types/${id}/tiers`, { tiers })).data,
      onSuccess: invalidate,
    }),
    /**
     * Retires a type.
     *
     * <p>Never a delete: a request approved last March names its type, a balance carries it,
     * and a payslip explained a deduction by it. What this does is stop the type being offered
     * on new requests while every one of those keeps resolving.
     */
    deactivate: useMutation({
      mutationFn: async (id: string) =>
        (await apiClient.post<LeaveTypeDetailView>(`${BASE}/admin/types/${id}/deactivate`)).data,
      onSuccess: invalidate,
    }),
    seedUae: useMutation({
      mutationFn: async (legalEntityId: string) =>
        (await apiClient.post<SeedPackResult>(`${BASE}/admin/types/seed/uae`, { legalEntityId })).data,
      onSuccess: invalidate,
    }),
  }
}

// --- Phase 7 part 5: everybody's balances ---

export interface OrgBalanceRow {
  employeeId: string
  employeeCode: string
  employeeName: string
  departmentId: string | null
  leaveTypeId: string | null
  leaveTypeCode: string | null
  leaveTypeName: string | null
  year: number
  entitled: number | null
  used: number | null
  pending: number | null
  available: number | null
  /** Set when the figures are null: no balance row exists, which is not a balance of zero. */
  note: string | null
  /** What the previous system said was left, when the balance was carried in. */
  broughtForward: number | null
  /** The day that figure was true on. */
  broughtForwardAsOf: string | null
  /** Days this system has accrued into the balance since. */
  accruedSince: number | null
}

export function useOrgLeaveBalances(params: {
  year?: number; entityId?: string; departmentId?: string; leaveTypeId?: string; q?: string
  page?: number; size?: number
}) {
  return useQuery({
    queryKey: ['leave', 'balances', 'all', params],
    queryFn: async () =>
      (await apiClient.get<PageResponse<OrgBalanceRow>>('/v1/leave/balances/all', {
        params: {
          year: params.year,
          entityId: params.entityId || undefined,
          departmentId: params.departmentId || undefined,
          leaveTypeId: params.leaveTypeId || undefined,
          q: params.q || undefined,
          page: params.page,
          size: params.size,
        },
      })).data,
    placeholderData: keepPreviousData,
  })
}

/** What a run of leave accrual did. */
export interface AccrualRunResult {
  employees: number
  entries: number
  daysAccrued: number
  daysCarried: number
  daysLapsed: number
}

/**
 * Catches this year's accrual up to the current month. Idempotent on the server: what is
 * already credited is not credited again, so pressing it twice is harmless.
 */
export function useRunAccrual() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => (await apiClient.post<AccrualRunResult>('/v1/leave/accrual/run')).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['leave', 'balances'] }),
  })
}

/** When HUMIS starts accruing leave, and what a run today would credit. */
export interface AccrualSettings {
  startsOn: string
  /** False while the date is the default: the first day of the month the company was created. */
  chosen: boolean
  creditsFrom: string
  creditsThrough: string
}

export function useAccrualSettings() {
  return useQuery({
    queryKey: ['leave', 'accrual', 'settings'],
    queryFn: async () => (await apiClient.get<AccrualSettings>('/v1/leave/accrual/settings')).data,
  })
}

export function useChangeAccrualStart() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (startsOn: string) =>
      (await apiClient.put<AccrualSettings>('/v1/leave/accrual/settings', { startsOn })).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['leave', 'accrual', 'settings'] }),
  })
}
